import { useCallback, useEffect, useRef, useState } from 'react';
import { loadZoomSdk } from '../../../utils/loadZoomSdk';
import { captureError, captureMessage } from '../../../utils/sentry';
import { classifyMediaError, extractErrorMessage } from '../../../utils/classifyMediaError';
import { checkMediaPermissionState, PermissionCheckResult } from '../../../utils/checkMediaPermissionState';

export type ZoomVideoStatus =
  | 'idle'
  | 'loading-sdk'
  | 'sdk-ready'
  | 'initializing'
  | 'ready'
  | 'connecting'
  | 'active'
  | 'ended';

export type ZoomErrorType =
  | 'scriptLoadFailed'
  | 'initFailed'
  | 'videoFailed'
  | 'permissionDenied'
  | 'deviceNotFound'
  | 'deviceNotReadable'
  | 'deviceOverconstrained'
  | 'aborted'
  | 'insecureContext';

// startVideo() can hang forever with no signal at all — no resolve, reject,
// or video-start/end/force-end event — leaving `status` stuck on 'connecting'
// with no Retry option (ZoomConnectingScreen only offers Retry once `error`
// is set). 20s is generous for ordinary connection setup; the agent wait
// itself happens later, once 'active'.
const CONNECT_VIDEO_TIMEOUT_MS = 20000;

export function classifyZoomStartError(
  err: unknown,
  phase: 'init' | 'video'
): { errorType: ZoomErrorType; category: string; domExceptionName?: string } {
  const classified = classifyMediaError(err);
  if (classified) return classified;

  const domExceptionName = err instanceof DOMException ? err.name : undefined;
  return { errorType: phase === 'init' ? 'initFailed' : 'videoFailed', category: 'zoom_sdk_error', domExceptionName };
}

async function logPermissionStateIfDenied() {
  const state = await checkMediaPermissionState();
  if (!state) return null;
  if (state.camera === 'denied' || state.microphone === 'denied') {
    captureMessage(
      'Camera/microphone permission already denied before Zoom join attempt',
      'camera_microphone_permission',
      {
        camera_state: state.camera,
        microphone_state: state.microphone,
      }
    );
  }
  return state;
}

/** The one device that is denied while the other isn't — undefined for none or both. */
export function deniedDevice(state: PermissionCheckResult | null) {
  if (!state) return undefined;
  if (state.camera === 'denied' && state.microphone !== 'denied') return 'camera';
  if (state.microphone === 'denied' && state.camera !== 'denied') return 'microphone';
  return undefined;
}

export interface ZoomVideoError {
  type: ZoomErrorType;
  message: string;
  /**
   * For a permission failure: which device was already denied when Zoom was
   * asked to start, if that was exactly one of them. Lets the error screen
   * say "Camera access is blocked" instead of the combined wording for a
   * customer who joined without a camera (the pre-join screen allows that).
   */
  device?: 'camera' | 'microphone';
}

export interface UseZoomVideoReturn {
  status: ZoomVideoStatus;
  error: ZoomVideoError | null;
  start: () => Promise<void>;
  /** Resets the hook to a clean state and re-drives the load → ready flow. */
  restart: () => void;
}

export default function useZoomVideo(entryId: string): UseZoomVideoReturn {
  const [status, setStatus] = useState<ZoomVideoStatus>('idle');
  const [error, setError] = useState<ZoomVideoError | null>(null);
  // Bumping `attempt` re-runs the load effect below, which is what restart() hangs off of.
  const [attempt, setAttempt] = useState(0);
  const clientRef = useRef<VideoClient | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Fresh attempt: drop any prior client/observer and clear the error so
    // the connecting UI shows again while we re-load. loadZoomSdk is
    // idempotent — a prior successful load resolves instantly; a prior
    // *failed* load (which reset its internal promise) genuinely re-attempts
    // the script fetch.
    observerRef.current?.disconnect();
    observerRef.current = null;
    clientRef.current = null;
    setError(null);
    setStatus('loading-sdk');

    loadZoomSdk()
      .then(() => {
        if (!cancelled) setStatus('sdk-ready');
      })
      .catch((err) => {
        if (!cancelled) {
          setError({
            type: 'scriptLoadFailed',
            message: err instanceof Error ? err.message : 'Failed to load Zoom SDK',
          });
        }
      });

    return () => {
      cancelled = true;
      observerRef.current?.disconnect();
      // Orphans this attempt for any watchdog timer still pending in a start()
      // closure below — it checks this identity before doing anything, so a
      // leftover timer just no-ops a little late instead of needing to be
      // proactively canceled here.
      clientRef.current = null;
    };
  }, [attempt]);

  const restart = useCallback(() => {
    setAttempt((a) => a + 1);
  }, []);

  const start = useCallback(async () => {
    if (status !== 'sdk-ready') return;

    // Fire-and-forget diagnostic, but the result is kept: the pre-join screen
    // now lets a customer with a blocked camera join, and this is how we
    // measure whether Zoom actually accepts that — the error report and the
    // video-start message below both carry it. Defensive .catch so it can
    // never leak an unhandled rejection into the join flow.
    let permissionStateAtJoin: PermissionCheckResult | null = null;
    logPermissionStateIfDenied()
      .then((state) => {
        permissionStateAtJoin = state;
      })
      .catch(() => {});

    const reportStartDespiteDenial = () => {
      const state = permissionStateAtJoin;
      const device = deniedDevice(state);
      if (!state || !device) return;
      captureMessage(
        'Zoom video started with a camera/microphone permission still denied',
        'camera_microphone_permission',
        { denied_device: device, camera_state: state.camera, microphone_state: state.microphone }
      );
    };

    let phase: 'init' | 'video' = 'init';
    let client: VideoClient | null = null;
    let hasReportedError = false;
    let connectTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const clearConnectTimeout = () => {
      if (connectTimeoutId) {
        clearTimeout(connectTimeoutId);
        connectTimeoutId = null;
      }
    };

    const reportStartError = (err: unknown, errorPhase: 'init' | 'video') => {
      // A single attempt can trigger this more than once for one incident
      // (e.g. the watchdog times out, then startVideo() itself later
      // rejects) — report only the first.
      if (hasReportedError) return;
      hasReportedError = true;

      const { errorType, category, domExceptionName } = classifyZoomStartError(err, errorPhase);

      // Can be true if a restart() already orphaned this attempt. Tagged
      // rather than suppressed: every failure still gets reported for
      // frequency visibility, but triage can filter out stale ones.
      const isStale = !!(client && clientRef.current !== client);

      const tags = {
        zoom_error_type: errorType,
        phase: errorPhase,
        stale_attempt: String(isStale),
        ...(domExceptionName ? { dom_exception_name: domExceptionName } : {}),
        ...(permissionStateAtJoin
          ? {
              camera_state_at_join: permissionStateAtJoin.camera,
              microphone_state_at_join: permissionStateAtJoin.microphone,
            }
          : {}),
      };
      // Errors/DOMExceptions already carry their diagnostic info via .message;
      // a plain rejection payload (e.g. Zoom's {errorCode, reason, type} shape)
      // doesn't, so attach it as extra context instead of losing it.
      if (!(err instanceof Error) && !(err instanceof DOMException) && err && typeof err === 'object') {
        captureError(err, category, tags, err as Record<string, unknown>);
      } else {
        captureError(err, category, tags);
      }

      // Don't let a stale attempt's error clobber a newer attempt's UI state
      // (still reported above for diagnostics either way).
      if (isStale) return;

      // startVideo() isn't awaited, so the observer below is already attached
      // by the time a rejection arrives here. Disconnect it for this failed
      // attempt — otherwise it keeps watching the whole document while the
      // error screen shows, and could later flip status to 'ended' and swap
      // it for the Goodbye screen.
      observerRef.current?.disconnect();
      observerRef.current = null;

      // The video-end/video-force-end listeners registered below stay
      // attached too — clearing this stops a late event from this failed
      // attempt passing their clientRef.current === client check.
      clientRef.current = null;

      setError({
        type: errorType,
        message: extractErrorMessage(err),
        ...(errorType === 'permissionDenied' ? { device: deniedDevice(permissionStateAtJoin) } : {}),
      });
    };

    try {
      setStatus('initializing');
      client = new window.VideoClient({});
      clientRef.current = client;

      await client.init({ entryId });
      // A restart() may have nulled clientRef out from under this in-flight
      // attempt while init() was pending — bail out rather than reviving a
      // stale attempt's UI state on top of whatever the newer attempt is doing.
      if (clientRef.current !== client) return;
      setStatus('ready');

      // The Zoom SDK exposes no documented way to detach these listeners (no
      // .off()/destroy() in zoom-types.d.ts or the loaded SDK bundle), so a
      // restarted/orphaned client can still fire these events later. Guard
      // each callback by identity so a stale client's late event can never
      // overwrite a newer attempt's status.
      client.on('video-start', () => {
        if (clientRef.current === client) {
          clearConnectTimeout();
          setStatus('active');
          reportStartDespiteDenial();
        }
      });
      client.on('video-end', () => {
        if (clientRef.current === client) {
          clearConnectTimeout();
          setStatus('ended');
        }
      });
      client.on('video-force-end', () => {
        if (clientRef.current === client) {
          clearConnectTimeout();
          setStatus('ended');
        }
      });

      phase = 'video';
      setStatus('connecting');
      client.startVideo().then(
        () => {
          if (clientRef.current === client) clearConnectTimeout();
        },
        (err) => {
          if (clientRef.current === client) clearConnectTimeout();
          reportStartError(err, 'video');
        }
      );

      // Cleared above by whichever settles first. The identity check inside
      // is belt-and-suspenders: even if this timer somehow survived a
      // restart/unmount, this stops it from reaching reportStartError at all.
      connectTimeoutId = setTimeout(() => {
        connectTimeoutId = null;
        if (clientRef.current !== client) return;
        reportStartError(new Error('Zoom video connection timed out'), 'video');
      }, CONNECT_VIDEO_TIMEOUT_MS);

      // The SDK doesn't fire video-end when the user leaves from the
      // waiting room (before video-start). It removes its
      // #__zoom_video_client__ div silently. Watch for that removal.
      // 3s arming delay ignores the SDK's initial DOM setup shuffling.
      let armed = false;
      setTimeout(() => { armed = true; }, 3000);

      // Not currently reachable (an active observer implies no error was
      // thrown for this attempt, and restart() is only ever triggered from a
      // Retry button that requires an error) — guarded anyway since that's an
      // implicit invariant, not an enforced one, and this is cheap insurance
      // against a stale observer acting on a since-reassigned observerRef.
      observerRef.current = new MutationObserver((mutations) => {
        if (!armed || clientRef.current !== client) return;
        for (const mutation of mutations) {
          for (let i = 0; i < mutation.removedNodes.length; i++) {
            const node = mutation.removedNodes[i];
            if (node instanceof HTMLElement && node.id === '__zoom_video_client__') {
              observerRef.current?.disconnect();
              clearConnectTimeout();
              setStatus('ended');
              return;
            }
          }
        }
      });
      observerRef.current.observe(document.body, { childList: true, subtree: true });
    } catch (err) {
      reportStartError(err, phase);
    }
  }, [status, entryId]);

  return { status, error, start, restart };
}
